import React from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import Link from 'components/AppSafeLink/AppSafeLink';
import { Image } from '@apmg/mimas';
import { Body } from '@apmg/amat';
import { globals } from '../../config/globals';
import { fishForSocialMediaImage } from '../../components/Metatags/MetaTagHelpers';
import Metatags from '../../components/Metatags/Metatags';
import ContributionsContainer from './ContributionsContainer';
import ApmRelatedLinkListItemOverride from '../../components/AmatOverrides/ApmRelatedLinkListItemOverride';
import ApmRelatedLinkOverride from '../../components/AmatOverrides/ApmRelatedLinkOverride';
import LinkOverride from '../../components/AmatOverrides/LinkOverride';
import '/public/css/pages/person.css';

const Person = ({ data: { profile }, pageNum }) => {
  const personSchema = {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    mainEntity: {
      '@type': 'Person',
      name: profile?.title,
      description: profile?.descriptionText,
      url: `https://www.mprnews.org/people/${profile.canonicalSlug}`,
      image: {
        '@type': 'ImageObject',
        url:
          profile?.primaryVisuals?.lead?.fallback ||
          profile?.primaryVisuals?.social?.fallback,
        contentUrl:
          profile?.primaryVisuals?.lead?.fallback ||
          profile?.primaryVisuals?.social?.fallback,
      },
    },
  };

  const noFollowIndex = profile?.results?.currentPage > 1;

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
        />
      </Head>
      <Metatags
        title={profile.title}
        fullSlug={`people/${profile.canonicalSlug}`}
        description={profile.descriptionText}
        image={fishForSocialMediaImage(profile)?.url}
        topic={profile.primaryCollection?.title}
        originalSourceUrl={profile.originalSourceUrl}
        noFollow={noFollowIndex}
      />

      <section className="person-page">
        <div className="content">
          <div className="person">
            <div className="person-header">
              {profile?.primaryVisuals?.lead && (
                <Image
                  key={profile?.primaryVisuals.lead.fallback}
                  image={profile?.primaryVisuals.lead}
                  aspectRatio="uncropped"
                  sizes={globals.sizes.primaryVisuals}
                  alt={profile?.primaryVisuals.lead.shortCaption}
                />
              )}

              <div className="person-info">
                <h2 className="hdg-profile">{profile?.title}</h2>
                {profile?.jobTitle && (
                  <h3 className="hdg-profile">{profile?.jobTitle}</h3>
                )}

                {profile?.email && (
                  <Link
                    className="btn btn-primary"
                    href={`mailto:${profile?.email}`}
                  >
                    Email {profile?.title}
                  </Link>
                )}
                {Boolean(profile?.profileRelatedLinks?.length) && (
                  <div className="person-related-links">
                    {profile.profileRelatedLinks.map((link) => {
                      return (
                        <Link
                          href={link.uri}
                          key={link.uri}
                          className="btn btn-primary btn-slim"
                        >
                          {link.text}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className="person-body">
              {profile && profile.body && (
                <>
                  <h3>Meet {profile?.title}</h3>
                  <Body
                    nodeData={JSON.parse(profile?.body)}
                    embedded={profile?.embeddedAssets}
                    overrides={{
                      link: LinkOverride,
                      apm_related_link: ApmRelatedLinkOverride,
                      apm_related_link_list_item:
                        ApmRelatedLinkListItemOverride,
                    }}
                  />
                </>
              )}
            </div>
            <ContributionsContainer
              initialCollection={profile.results}
              initialPage={pageNum}
              slug={profile.canonicalSlug}
              id={profile.id}
            />
          </div>
        </div>
      </section>
    </>
  );
};

Person.propTypes = {
  data: PropTypes.shape({
    profile: PropTypes.object,
  }),
  pageNum: PropTypes.number,
  originalSourceUrl: PropTypes.string,
};

export default Person;
